/**
 * Shared validators for the lease-input components (wizard Step 3/7 and the amendment flow).
 * Moved verbatim from Step7PetDetails.tsx / Step3Tenants.tsx so both surfaces validate identically.
 */

import type { PetDetail, AssistanceAnimal } from '../../types/lease-generation';

export const BLANK_ANIMAL: PetDetail = {
  type: 'dog',   // first field; default to dog per spec
  name: '',
  // all other fields are now optional — omit from blank template
};

export const BLANK_ASSISTANCE_ANIMAL: AssistanceAnimal = {
  name: '',
  species: '',
  breed: '',
  type: 'esa',
};

export function isAssistanceAnimalComplete(a: AssistanceAnimal): boolean {
  if (!a.name.trim() || !a.species.trim() || !a.breed.trim()) return false;
  if (a.type === 'esa') {
    return !!a.providerName?.trim() && !!a.providerLicense?.trim() && !!a.documentationDate;
  }
  if (a.type === 'service_animal') {
    return !!a.taskDescription?.trim();
  }
  return true;
}

/**
 * Type-conditional validation for a pet entry.
 * Mirrors the superRefine logic in PetDetailSchema — divergence would allow
 * the wizard to proceed when the API will reject.
 */
export function petHasError(a: PetDetail): boolean {
  if (!a.name.trim()) return true;
  const t = a.type;
  if (t === 'dog' || t === 'cat') {
    if (!a.breed?.trim()) return true;
    if (!a.sex) return true;
    if (!a.weight || a.weight <= 0) return true;
    if (a.spayedNeutered == null) return true;
    if (a.vaccinationsCurrent == null) return true;
  }
  if (t === 'bird') {
    if (!a.species?.trim()) return true;
    if (!a.cageDescription?.trim()) return true;
  }
  if (t === 'reptile') {
    if (!a.species?.trim()) return true;
    if (!a.enclosureDescription?.trim()) return true;
    if (a.venomous == null) return true;
  }
  if (t === 'fish') {
    if (!a.tankSizeGallons || a.tankSizeGallons <= 0) return true;
  }
  if (t === 'small_mammal') {
    if (!a.species?.trim()) return true;
  }
  return false;
}

/**
 * Validate the add-co-signer form. Returns the first error message, or null when valid.
 */
export function validateCoSigner(
  form: { name: string; email: string; phone?: string; mailingAddress: string },
  opts?: { requirePhone?: boolean }
): string | null {
  if (!form.name.trim()) return 'Full name is required';
  if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    return 'A valid email address is required';
  }
  if (opts?.requirePhone && !form.phone?.trim()) return 'Phone number is required';
  if (!form.mailingAddress.trim()) return 'Mailing address is required';
  return null;
}
